import { useCallback, useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { EnhancedTable, useClientTable } from "@pushpress/shared-components";
import { DataTableProps } from "@pushpress/shared-components/build/components/data/DataTable";
import AppointmentStatusChip from "../AppointmentStatusChip/AppointmentStatusChip";
import { CreditFragment } from "./AppointmentCredits-generated.hooks";
import { ObjectToBeDefined } from "../../../types/types";

interface CreditsTableProps<K extends string>
  extends Pick<
    DataTableProps<CreditFragment, K>,
    "data" | "renderRowMenu" | "loading"
  > {
  data: CreditFragment[];
}

function CreditsTable<K extends string>(props: CreditsTableProps<K>) {
  const { data: dataProp, ...other } = props;

  const columns = useMemo(
    () => [
      {
        key: "appointmentType",
        header: "Type",
        isSortable: true,
        sort: (a: CreditFragment, b: CreditFragment) =>
          a?.appointmentType?.name?.localeCompare(b?.appointmentType?.name),
        cell: (row: CreditFragment) => {
          return row?.appointmentType?.name || "- -";
        },
      },
      {
        header: "Expiration",
        key: "expiresAt",
        hideOnMobile: true,
        isSortable: true,
        width: "33%",
        cell: (row: CreditFragment) => {
          if (!row.expiresAt || isNaN(new Date(row.expiresAt).getTime())) {
            return "- -";
          }
          return formatInTimeZone(
            new Date(row.expiresAt),
            "UTC",
            "EEEE, MMM dd, yyyy",
          );
        },
      },
      {
        header: "Status",
        key: "eventStatus",
        hideOnMobile: true,
        isSortable: true,
        width: "25%",
        cell: (row: CreditFragment) => <AppointmentStatusChip data={row} />,
      },
    ],
    [],
  );

  const { data, sort, page, pageCount, handleSort, handlePageChange } =
    useClientTable({
      data: dataProp || [],
      columns,
      defaultSort: { key: "expiresAt", order: "asc" },
      defaultRowsPerPage: 20,
    });

  const getRowId = useCallback((row: ObjectToBeDefined) => {
    return row.uuid;
  }, []);

  return (
    <EnhancedTable
      {...other}
      data={data}
      columns={columns}
      sort={sort}
      page={page}
      pageCount={pageCount}
      aria-label="Credits table"
      getRowId={getRowId}
      onSort={handleSort}
      onPageChange={handlePageChange}
    />
  );
}

export default CreditsTable;
